import React, { useReducer, useEffect, useContext, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import { translateRole } from "../../i18n";
import UserAvatar from "./UserAvatar";

const initialState = {
  drop11: false,
  drop12: false,
  drop13: false,
  menuExpand: null,
};

function reducer(state, action) {
  const newState = { ...state };
  switch (action.type) {
    case "toggle":
      for (const key in newState) {
        if (key === action.menu) {
          newState[key] = !newState[key];
          newState.menuExpand = newState[key] ? action.menu : null;
        } else if (key !== "menuExpand") {
          newState[key] = false;
        }
      }
      return newState;
    case "open":
      for (const key in newState) {
        if (key === action.menu) {
          newState[key] = true;
          newState.menuExpand = action.menu;
        } else if (key !== "menuExpand") {
          newState[key] = false;
        }
      }
      return newState;
    case "closeAll":
      return {
        drop11: false,
        drop12: false,
        drop13: false,
        menuExpand: null,
      };
    default:
      return state;
  }
}

function Sidebar() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isHovered, setIsHovered] = useState(false);
  const location = useLocation();

  const hoverSubmenuTimeoutRef = useRef(null);
  const leaveTimeoutRef = useRef(null);
  const prevWidthRef = useRef(window.innerWidth);

  const isPathActive = (path) => location.pathname.startsWith(path);

  // Set default state to compact (sidebar-icon-only) on desktop
  useEffect(() => {
    if (window.innerWidth >= 992) {
      document.body.classList.add("sidebar-icon-only");
    }

    const handleResize = () => {
      const currentWidth = window.innerWidth;
      const prevWidth = prevWidthRef.current;
      if (prevWidth < 992 && currentWidth >= 992) {
        document.body.classList.add("sidebar-icon-only");
      } else if (prevWidth >= 992 && currentWidth < 992) {
        document.body.classList.remove("sidebar-icon-only");
      }
      prevWidthRef.current = currentWidth;
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (hoverSubmenuTimeoutRef.current) clearTimeout(hoverSubmenuTimeoutRef.current);
      if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
    };
  }, []);

  // When location changes, close hover state and submenus cleanly if in icon-only mode
  useEffect(() => {
    if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
    if (hoverSubmenuTimeoutRef.current) clearTimeout(hoverSubmenuTimeoutRef.current);

    if (document.body.classList.contains("sidebar-icon-only")) {
      setIsHovered(false);
      dispatch({ type: "closeAll" });
    }
  }, [location.pathname]);

  const handleSidebarMouseEnter = () => {
    if (window.innerWidth < 992) return;

    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }

    setIsHovered(true);
  };

  const handleSidebarMouseLeave = () => {
    if (window.innerWidth < 992) return;

    if (hoverSubmenuTimeoutRef.current) {
      clearTimeout(hoverSubmenuTimeoutRef.current);
      hoverSubmenuTimeoutRef.current = null;
    }

    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
    }

    leaveTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
      if (document.body.classList.contains("sidebar-icon-only")) {
        dispatch({ type: "closeAll" });
      }
    }, 200);
  };

  const handleSubmenuHover = (menuKey) => {
    if (window.innerWidth < 992) return;

    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }

    if (hoverSubmenuTimeoutRef.current) {
      clearTimeout(hoverSubmenuTimeoutRef.current);
    }

    hoverSubmenuTimeoutRef.current = setTimeout(() => {
      dispatch({ type: "open", menu: menuKey });
    }, 80);
  };

  const handleRootItemHover = () => {
    if (window.innerWidth < 992) return;

    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }

    if (hoverSubmenuTimeoutRef.current) {
      clearTimeout(hoverSubmenuTimeoutRef.current);
    }

    hoverSubmenuTimeoutRef.current = setTimeout(() => {
      dispatch({ type: "closeAll" });
    }, 120);
  };

  const handleSubmenuClick = (menuKey) => {
    if (hoverSubmenuTimeoutRef.current) {
      clearTimeout(hoverSubmenuTimeoutRef.current);
      hoverSubmenuTimeoutRef.current = null;
    }
    dispatch({ type: "toggle", menu: menuKey });
  };

  return (
    <nav
      className={`sidebar sidebar-offcanvas ${isHovered ? "sidebar-hover-expanded" : ""}`}
      id="sidebar"
      onMouseEnter={handleSidebarMouseEnter}
      onMouseLeave={handleSidebarMouseLeave}
    >
      <div 
        className="sidebar-brand-wrapper"
        onMouseEnter={handleRootItemHover}
      >
        <Link className="sidebar-brand brand-logo" to="/dashboard">
          <img 
            src="/images/rutex-logo.svg" 
            alt="Rutex Logo" 
            onError={(e) => { e.target.onerror = null; e.target.src = '/images/logo.png'; }} 
          />
        </Link>
        <Link className="sidebar-brand brand-logo-mini" to="/dashboard">
          <img 
            src="/images/rutex-simbolo.svg" 
            alt="Rutex Simbolo" 
            onError={(e) => { e.target.onerror = null; e.target.src = '/images/logo-mini.png'; }} 
          />
        </Link>
      </div>

      <ul className="nav sidebar-main-nav">
        {/* User Profile */}
        <li 
          className="nav-item nav-profile"
          onMouseEnter={handleRootItemHover}
        >
          <Link to="/general-pages/profile" className="nav-profile-link">
            <UserAvatar user={user} size="sm" className="nav-profile-avatar" />
            <div className="nav-profile-info">
              <span className="profile-name">
                {user ? user.nombre_completo : 'Administrador'}
              </span>
              <span className="profile-role">
                {user ? translateRole(user.rol, t) : t('role_admin', 'Administrador')}
              </span>
            </div>
          </Link>
        </li>

        {/* Dashboard */}
        <li 
          className={`nav-item ${isPathActive('/dashboard') ? 'active' : ''}`}
          onMouseEnter={handleRootItemHover}
        >
          <Link className="nav-link-custom" to="/dashboard">
            <span className="menu-icon-wrapper">
              <i className="mdi mdi-home menu-icon"></i>
            </span>
            <span className="menu-title">{t('menu_dashboard', 'Dashboard')}</span>
          </Link>
        </li>

        {/* Ventas y Catálogo */}
        <li 
          className={`nav-item menu-items ${isPathActive("/ecommerce") || isPathActive("/apps/gallery") ? "active" : ""}`}
          onMouseEnter={() => handleSubmenuHover("drop12")}
        >
          <div
            className={`nav-link-custom ${state.drop12 ? "expanded" : ""}`}
            onClick={() => handleSubmenuClick("drop12")}
            role="button"
            tabIndex={0}
          >
            <span className="menu-icon-wrapper">
              <i className="mdi mdi-cart-outline menu-icon"></i>
            </span>
            <span className="menu-title">{t('menu_sales_catalog', 'Ventas y Catálogo')}</span>
            <i className={`menu-arrow mdi mdi-chevron-right ${state.drop12 ? "rotated" : ""}`}></i>
          </div>
          <div className={`sidebar-submenu-wrapper ${state.drop12 ? "open" : ""}`}>
            <div className="sidebar-submenu-inner">
              <ul className="sidebar-submenu-list">
                <li className="sidebar-submenu-item">
                  <Link 
                    className={`sidebar-submenu-link ${isPathActive("/ecommerce/orders") ? "active" : ""}`} 
                    to="/ecommerce/orders"
                  >
                    <span className="submenu-bullet"></span>
                    <span className="submenu-text">{t('menu_orders_console', 'Consola de Pedidos')}</span>
                  </Link>
                </li>
                <li className="sidebar-submenu-item">
                  <Link 
                    className={`sidebar-submenu-link ${isPathActive("/ecommerce/seller-analytics") ? "active" : ""}`} 
                    to="/ecommerce/seller-analytics"
                  >
                    <span className="submenu-bullet"></span>
                    <span className="submenu-text">{t('menu_seller_analytics', 'Rendimiento de Vendedores')}</span>
                  </Link>
                </li>
                <li className="sidebar-submenu-item">
                  <Link 
                    className={`sidebar-submenu-link ${isPathActive("/ecommerce/catalogue") ? "active" : ""}`} 
                    to="/ecommerce/catalogue"
                  >
                    <span className="submenu-bullet"></span>
                    <span className="submenu-text">{t('menu_product_catalog', 'Catálogo de Productos')}</span>
                  </Link>
                </li>
                <li className="sidebar-submenu-item">
                  <Link 
                    className={`sidebar-submenu-link ${isPathActive("/apps/gallery") ? "active" : ""}`} 
                    to="/apps/gallery"
                  >
                    <span className="submenu-bullet"></span>
                    <span className="submenu-text">{t('menu_media_gallery', 'Galería Multimedia')}</span>
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </li>

        {/* Clientes y Logística */}
        <li 
          className={`nav-item menu-items ${isPathActive("/general-pages/customer-listing") || isPathActive("/general-pages/zones-manager") || isPathActive("/general-pages/route-planner") || isPathActive("/general-pages/agenda-monitor") || isPathActive("/general-pages/merchandising-reports") ? "active" : ""}`}
          onMouseEnter={() => handleSubmenuHover("drop11")}
        >
          <div
            className={`nav-link-custom ${state.drop11 ? "expanded" : ""}`}
            onClick={() => handleSubmenuClick("drop11")}
            role="button"
            tabIndex={0}
          >
            <span className="menu-icon-wrapper">
              <i className="mdi mdi-truck-delivery menu-icon"></i>
            </span>
            <span className="menu-title">{t('menu_clients_logistics', 'Clientes y Logística')}</span>
            <i className={`menu-arrow mdi mdi-chevron-right ${state.drop11 ? "rotated" : ""}`}></i>
          </div>
          <div className={`sidebar-submenu-wrapper ${state.drop11 ? "open" : ""}`}>
            <div className="sidebar-submenu-inner">
              <ul className="sidebar-submenu-list">
                <li className="sidebar-submenu-item">
                  <Link 
                    className={`sidebar-submenu-link ${isPathActive("/general-pages/customer-listing") ? "active" : ""}`} 
                    to="/general-pages/customer-listing"
                  >
                    <span className="submenu-bullet"></span>
                    <span className="submenu-text">{t('menu_clients', 'Clientes')}</span>
                  </Link>
                </li>
                <li className="sidebar-submenu-item">
                  <Link 
                    className={`sidebar-submenu-link ${isPathActive("/general-pages/zones-manager") ? "active" : ""}`} 
                    to="/general-pages/zones-manager"
                  >
                    <span className="submenu-bullet"></span>
                    <span className="submenu-text">{t('menu_zones_territories', 'Zonas y Territorios')}</span>
                  </Link>
                </li>
                <li className="sidebar-submenu-item">
                  <Link 
                    className={`sidebar-submenu-link ${isPathActive("/general-pages/route-planner") ? "active" : ""}`} 
                    to="/general-pages/route-planner"
                  >
                    <span className="submenu-bullet"></span>
                    <span className="submenu-text">{t('menu_route_planner', 'Planificador de Rutas')}</span>
                  </Link>
                </li>
                <li className="sidebar-submenu-item">
                  <Link 
                    className={`sidebar-submenu-link ${isPathActive("/general-pages/agenda-monitor") ? "active" : ""}`} 
                    to="/general-pages/agenda-monitor"
                  >
                    <span className="submenu-bullet"></span>
                    <span className="submenu-text">{t('menu_agenda_monitor', 'Agenda de Rutas')}</span>
                  </Link>
                </li>
                <li className="sidebar-submenu-item">
                  <Link 
                    className={`sidebar-submenu-link ${isPathActive("/general-pages/merchandising-reports") ? "active" : ""}`} 
                    to="/general-pages/merchandising-reports"
                  >
                    <span className="submenu-bullet"></span>
                    <span className="submenu-text">{t('menu_merchandising_reports', 'Auditoría de Anaquel')}</span>
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </li>

        {/* Personal y Control */}
        <li 
          className={`nav-item menu-items ${isPathActive("/general-pages/profile") || isPathActive("/general-pages/user-listing") || isPathActive("/general-pages/master-data") || isPathActive("/general-pages/faqs") ? "active" : ""}`}
          onMouseEnter={() => handleSubmenuHover("drop13")}
        >
          <div
            className={`nav-link-custom ${state.drop13 ? "expanded" : ""}`}
            onClick={() => handleSubmenuClick("drop13")}
            role="button"
            tabIndex={0}
          >
            <span className="menu-icon-wrapper">
              <i className="mdi mdi-account-multiple-outline menu-icon"></i>
            </span>
            <span className="menu-title">{t('menu_staff_control', 'Personal y Control')}</span>
            <i className={`menu-arrow mdi mdi-chevron-right ${state.drop13 ? "rotated" : ""}`}></i>
          </div>
          <div className={`sidebar-submenu-wrapper ${state.drop13 ? "open" : ""}`}>
            <div className="sidebar-submenu-inner">
              <ul className="sidebar-submenu-list">
                <li className="sidebar-submenu-item">
                  <Link 
                    className={`sidebar-submenu-link ${isPathActive("/general-pages/profile") ? "active" : ""}`} 
                    to="/general-pages/profile"
                  >
                    <span className="submenu-bullet"></span>
                    <span className="submenu-text">{t('menu_my_profile', 'Mi Perfil')}</span>
                  </Link>
                </li>
                <li className="sidebar-submenu-item">
                  <Link 
                    className={`sidebar-submenu-link ${isPathActive("/general-pages/user-listing") ? "active" : ""}`} 
                    to="/general-pages/user-listing"
                  >
                    <span className="submenu-bullet"></span>
                    <span className="submenu-text">{t('menu_staff_roles', 'Personal y Roles')}</span>
                  </Link>
                </li>
                <li className="sidebar-submenu-item">
                  <Link 
                    className={`sidebar-submenu-link ${isPathActive("/general-pages/master-data") ? "active" : ""}`} 
                    to="/general-pages/master-data"
                  >
                    <span className="submenu-bullet"></span>
                    <span className="submenu-text">{t('menu_master_data', 'Datos Maestros')}</span>
                  </Link>
                </li>
                <li className="sidebar-submenu-item">
                  <Link 
                    className={`sidebar-submenu-link ${isPathActive("/general-pages/faqs") ? "active" : ""}`} 
                    to="/general-pages/faqs"
                  >
                    <span className="submenu-bullet"></span>
                    <span className="submenu-text">{t('menu_faqs', 'Preguntas Frecuentes (FAQ)')}</span>
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </li>
      </ul>
    </nav>
  );
}

export default Sidebar;
